import { useState, useEffect, useCallback } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Pencil, Save, X, AlertTriangle, AlertCircle, History, DatabaseZap, FileWarning } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const authHdr = (token) => ({ Authorization: `Bearer ${token}` });

const EDITABLE_FIELDS = ["question_text", "passage_text", "options", "correct_answer", "explanation", "revise_recap_content", "diagram_description"];
const READONLY_META_FIELDS = [
  "grade", "subject", "topic", "sub_topic", "dataset_type", "question_pool",
  "question_type", "answer_format", "blooms_level", "difficulty", "question_position",
  "is_trap", "diagnostic_signal", "error_type", "language", "strand", "track",
  "chapter_type", "topic_type", "has_diagram", "diagram_type", "diagram_position",
  "revise_recap_format", "chapter_number", "topic_order", "sub_topic_order",
  "is_shared", "source_board", "source_school", "prerequisite_ids",
  "concept_dependencies", "dep_codes", "forward_unlocks_grades", "unlocks", "option_labels",
];

function fmtVal(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  if (typeof v === "object") return JSON.stringify(v);
  if (typeof v === "boolean") return v ? "true" : "false";
  return String(v);
}

export default function RecordDetailModal({ recordId, filename, token, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [validation, setValidation] = useState(null);
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadDetail = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API}/api/admin/cr/dataset-ops/records/${encodeURIComponent(recordId)}?source_file=${encodeURIComponent(filename)}`,
        { headers: authHdr(token) }
      );
      const json = await res.json();
      if (!res.ok) { toast.error(json.detail || "Failed to load record"); onClose(); return; }
      setDetail(json);
      const r = json.record;
      setForm({
        question_text: r.question_text || "",
        passage_text: r.passage_text || "",
        options: r.options || null,
        correct_answer: r.correct_answer || "",
        explanation: r.explanation || "",
        revise_recap_content: r.revise_recap_content || "",
        diagram_description: r.diagram_description || "",
      });
    } catch { toast.error("Network error loading record"); }
    finally { setLoading(false); }
  }, [recordId, filename, token, onClose]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/cr/dataset-ops/records/${encodeURIComponent(recordId)}/edit-history`, {
        headers: authHdr(token),
      });
      const json = await res.json();
      setHistory(json.edits || []);
    } catch { toast.error("Failed to load edit history"); }
    finally { setHistoryLoading(false); }
  }, [recordId, token]);

  const handleSave = async () => {
    setSaving(true);
    setValidation(null);
    try {
      const payload = EDITABLE_FIELDS.reduce((acc, f) => ({ ...acc, [f]: form[f] }), {});
      const res = await fetch(`${API}/api/admin/cr/dataset-ops/records/${encodeURIComponent(recordId)}`, {
        method: "PATCH",
        headers: { ...authHdr(token), "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (!res.ok) { toast.error(json.detail || "Save failed"); return; }
      setValidation(json.validation);
      if (!json.ok) { toast.error("Validation failed — fix the errors below"); return; }
      setDetail({ source: "fuel_questions", record: json.record });
      setEditing(false);
      toast.success(json.changed_fields.length ? "Saved" : "No changes to save");
      onSaved?.();
    } catch { toast.error("Network error during save"); }
    finally { setSaving(false); }
  };

  const record = detail?.record;
  const isUncommitted = detail?.source === "file";
  const optionKeys = record?.options ? Object.keys(record.options) : [];

  return (
    <Dialog open={!!recordId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="record-detail-modal">
        {loading || !record ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-300" /></div>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2 flex-wrap">
                <DialogTitle className="font-mono text-base">{record.record_id}</DialogTitle>
                {isUncommitted ? (
                  <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50 flex items-center gap-1" data-testid="record-source-badge">
                    <FileWarning className="w-3 h-3" /> Uncommitted — from file
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-emerald-700 border-emerald-300 bg-emerald-50 flex items-center gap-1" data-testid="record-source-badge">
                    <DatabaseZap className="w-3 h-3" /> Live DB
                  </Badge>
                )}
              </div>
            </DialogHeader>

            <Tabs defaultValue="content" onValueChange={(v) => v === "history" && !history && loadHistory()}>
              <TabsList>
                <TabsTrigger value="content" data-testid="record-tab-content">Content</TabsTrigger>
                <TabsTrigger value="history" data-testid="record-tab-history">
                  <History className="w-3.5 h-3.5 mr-1.5" /> Edit History
                </TabsTrigger>
              </TabsList>

              {/* ── Content tab ───────────────────────────────────────── */}
              <TabsContent value="content" className="space-y-5">
                {isUncommitted && (
                  <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-lg px-3 py-2">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                    This record hasn't been committed to fuel_questions yet. Commit the dataset first (Upload &amp; Validate → Commit) before editing.
                  </div>
                )}

                {/* Wiring / metadata — always read-only */}
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Metadata &amp; Wiring (read-only)</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 text-xs bg-slate-50 rounded-lg p-3" data-testid="record-metadata-grid">
                    {READONLY_META_FIELDS.map((f) => (
                      <div key={f}>
                        <span className="text-slate-400">{f}: </span>
                        <span className="text-slate-700">{fmtVal(record[f])}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Content fields */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Content</p>
                    {!editing && !isUncommitted && (
                      <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="record-edit-btn">
                        <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
                      </Button>
                    )}
                  </div>

                  {validation?.errors?.length > 0 && (
                    <div className="mb-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2 space-y-1" data-testid="record-validation-errors">
                      {validation.errors.map((e, i) => (
                        <div key={i} className="flex items-start gap-1.5"><AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{e}</div>
                      ))}
                    </div>
                  )}
                  {validation?.warnings?.length > 0 && (
                    <div className="mb-3 bg-amber-50 border border-amber-200 text-amber-700 text-xs rounded-lg px-3 py-2 space-y-1" data-testid="record-validation-warnings">
                      {validation.warnings.map((w, i) => (
                        <div key={i} className="flex items-start gap-1.5"><AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{w}</div>
                      ))}
                    </div>
                  )}

                  <div className="space-y-4">
                    {record.passage_text != null || form.passage_text ? (
                      <Field label="Passage Text" editing={editing}>
                        {editing ? (
                          <Textarea rows={3} value={form.passage_text} onChange={(e) => setForm(f => ({ ...f, passage_text: e.target.value }))} data-testid="record-field-passage_text" />
                        ) : <p className="text-sm text-slate-700 whitespace-pre-wrap">{fmtVal(record.passage_text)}</p>}
                      </Field>
                    ) : null}

                    {record.dataset_type !== "Brush-ReviseRecap" && (
                      <Field label="Question Text" editing={editing}>
                        {editing ? (
                          <Textarea rows={3} value={form.question_text} onChange={(e) => setForm(f => ({ ...f, question_text: e.target.value }))} data-testid="record-field-question_text" />
                        ) : <p className="text-sm text-slate-700 whitespace-pre-wrap">{fmtVal(record.question_text)}</p>}
                      </Field>
                    )}

                    {record.options && (
                      <Field label="Options" editing={editing}>
                        {editing ? (
                          <div className="grid grid-cols-2 gap-3">
                            {Object.keys(form.options || {}).map((k) => (
                              <div key={k} className="flex items-center gap-2">
                                <span className="text-xs font-bold text-slate-500 w-4 shrink-0">{k}</span>
                                <Input
                                  value={form.options[k] || ""}
                                  onChange={(e) => setForm(f => ({ ...f, options: { ...f.options, [k]: e.target.value } }))}
                                  data-testid={`record-field-option-${k}`}
                                />
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="space-y-1">
                            {Object.entries(record.options).map(([k, v]) => (
                              <div key={k} className={`text-sm flex gap-2 ${record.correct_answer === k ? "font-semibold text-emerald-700" : "text-slate-700"}`}>
                                <span className="w-4">{k}</span><span>{v}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </Field>
                    )}

                    {(record.dataset_type !== "Brush-ReviseRecap") && (
                      <Field label="Correct Answer" editing={editing}>
                        {editing ? (
                          optionKeys.length > 0 ? (
                            <Select value={form.correct_answer} onValueChange={(v) => setForm(f => ({ ...f, correct_answer: v }))}>
                              <SelectTrigger className="w-32" data-testid="record-field-correct_answer">
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                              <SelectContent>
                                {optionKeys.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input className="w-48" value={form.correct_answer} onChange={(e) => setForm(f => ({ ...f, correct_answer: e.target.value }))} data-testid="record-field-correct_answer" />
                          )
                        ) : <p className="text-sm font-semibold text-slate-700">{fmtVal(record.correct_answer)}</p>}
                      </Field>
                    )}

                    {record.dataset_type !== "Brush-ReviseRecap" && (
                      <Field label="Explanation" editing={editing}>
                        {editing ? (
                          <Textarea rows={3} value={form.explanation} onChange={(e) => setForm(f => ({ ...f, explanation: e.target.value }))} data-testid="record-field-explanation" />
                        ) : <p className="text-sm text-slate-700 whitespace-pre-wrap">{fmtVal(record.explanation)}</p>}
                      </Field>
                    )}

                    {record.dataset_type === "Brush-ReviseRecap" && (
                      <Field label="Revise Recap Content" editing={editing}>
                        {editing ? (
                          <Textarea rows={5} value={form.revise_recap_content} onChange={(e) => setForm(f => ({ ...f, revise_recap_content: e.target.value }))} data-testid="record-field-revise_recap_content" />
                        ) : <p className="text-sm text-slate-700 whitespace-pre-wrap">{fmtVal(record.revise_recap_content)}</p>}
                      </Field>
                    )}

                    {(record.has_diagram || form.diagram_description) && (
                      <Field label="Diagram Description" editing={editing}>
                        {editing ? (
                          <Textarea rows={2} value={form.diagram_description} onChange={(e) => setForm(f => ({ ...f, diagram_description: e.target.value }))} data-testid="record-field-diagram_description" />
                        ) : <p className="text-sm text-slate-700 whitespace-pre-wrap">{fmtVal(record.diagram_description)}</p>}
                      </Field>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* ── Edit History tab ──────────────────────────────────── */}
              <TabsContent value="history">
                {historyLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>
                ) : !history || history.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-8" data-testid="record-history-empty">No edits recorded for this record yet.</p>
                ) : (
                  <div className="space-y-3" data-testid="record-history-list">
                    {history.map((h, i) => (
                      <div key={i} className="border border-slate-200 rounded-lg p-3 text-xs" data-testid={`record-history-entry-${i}`}>
                        <div className="flex items-center justify-between mb-2 text-slate-500">
                          <span>{new Date(h.edited_at).toLocaleString()}</span>
                          <span>by {h.edited_by}</span>
                        </div>
                        <div className="space-y-1.5">
                          {Object.entries(h.changed_fields).map(([field, { old, new: newVal }]) => (
                            <div key={field} className="bg-slate-50 rounded px-2 py-1.5">
                              <p className="font-semibold text-slate-600 mb-0.5">{field}</p>
                              <p className="text-red-600 line-through decoration-red-300">{fmtVal(old)}</p>
                              <p className="text-emerald-700">{fmtVal(newVal)}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>

            {editing && (
              <DialogFooter>
                <Button variant="ghost" onClick={() => { setEditing(false); setValidation(null); }} disabled={saving} data-testid="record-cancel-btn">
                  <X className="w-3.5 h-3.5 mr-1.5" /> Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving} data-testid="record-save-btn">
                  {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
                  Save
                </Button>
              </DialogFooter>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">{label}</label>
      {children}
    </div>
  );
}
